/**
 * chart.js on one canvas, colored from the theme tokens.
 *
 * `build( color )` returns a chart.js config; `color( 'metric-a' )` reads
 * `--color-metric-a` off this chart's own box, not off <html>, because dark tokens
 * and an account's own colors are declared per element under `.dark`. The chart
 * is rebuilt when `build` changes or the theme does, so a caller memoizes `build`.
 *
 * Built in a microtask rather than straight from the effect: EsThemeProvider flips
 * the `dark` class in an effect of its own, and a parent's effects run after a
 * child's, so reading the colors here directly would paint the mode just left.
 */
import { useEffect, useRef } from 'react';
import { Chart, registerables } from 'chart.js';
import { useEsTheme } from './EsThemeContext';

Chart.register( ...registerables );

export const esTokenColor = ( pElement, pToken ) =>
    getComputedStyle( pElement ).getPropertyValue( '--color-' + pToken ).trim();

export default function EsChart( { build, className, 'data-testid': pTestId } ) {
    const boxRef = useRef( null );
    const canvasRef = useRef( null );
    const { resolved, colors } = useEsTheme();

    useEffect( () => {
        let aChart = null;
        let aGone = false;
        queueMicrotask( () => {
            if ( aGone || !canvasRef.current ) return;
            aChart = new Chart( canvasRef.current, build( ( pToken ) => esTokenColor( boxRef.current, pToken ) ) );
        } );
        return () => {
            aGone = true;
            aChart?.destroy();
        };
    }, [ build, resolved, colors ] );

    return (
        <div ref={ boxRef } className={ 'relative ' + ( className ?? 'h-[190px]' ) } data-testid={ pTestId }>
            <canvas ref={ canvasRef } />
        </div>
    );
}
